import React from 'react';
import PropTypes from 'prop-types';
import DirectionsBikeIcon from '@material-ui/icons/DirectionsBike';
import TerrainIcon from '@material-ui/icons/Terrain';
import {
  Create,
  Edit,
  Filter,
  ImageField,
  List,
  LongTextInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  required,
} from 'react-admin';
import Datagrid from './detail/Datagrid';
import ShowButton from './button/ShowButton';
import LongTextField from './fields/LongTextField';
import LLRUploadInput from './inputs/LLRUploadInput';
import ManyToManyInputHelper from './data/ManyToManyInputHelper';
import ReferenceManyToManyField from './data/ReferenceManyToManyField';
import MultiLangBlock from './other/MultiLangBlock';

const TITLE_SINGULAR = 'Region';
const TITLE_PLURAL = 'Regions';

const RouteOptionRenderer = ({ record }) => {
  if (!record) return null;
  const isBike = record.activityType === 'bike';
  const Icon = isBike ? DirectionsBikeIcon : TerrainIcon;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Icon style={{ fontSize: '1.2rem' }} />
      {record.internalName}
    </span>
  );
};
RouteOptionRenderer.propTypes = {
  record: PropTypes.shape({
    activityType: PropTypes.string,
    internalName: PropTypes.string,
  }),
};

const RegionTitle = ({ record }) => (
  <span>
    {TITLE_SINGULAR} "{record.name}"
  </span>
);
RegionTitle.propTypes = { record: PropTypes.shape({ name: PropTypes.string }) };

const RegionFilters = props => (
  <Filter {...props}>
    <TextInput label="Search" source="q__name" alwaysOn />
  </Filter>
);

export const RegionList = props => (
  <List
    title={TITLE_PLURAL}
    {...props}
    sort={{ field: 'name', order: 'ASC' }}
    filters={<RegionFilters />}
  >
    <Datagrid rowClick="edit">
      <TextField source="name" />
      <ImageField source="image" label="Image (Bike)" />
      <ImageField source="imageHike" label="Image (Hike)" />
      <ShowButton />
    </Datagrid>
  </List>
);

export const RegionShow = props => (
  <Show title={<RegionTitle />} {...props}>
    <SimpleShowLayout>
      <TextField source="name" />
      <div style={{ display: 'flex', gap: '20px' }}>
        <div style={{ flex: 1 }}>
          <ImageField source="image" label="Image (Bike)" />
        </div>
        <div style={{ flex: 1 }}>
          <ImageField source="imageHike" label="Image (Hike)" />
        </div>
      </div>
      <MultiLangBlock isShow>
        <LongTextField source="description" label="Description (Bike)" />
      </MultiLangBlock>
      <MultiLangBlock isShow>
        <LongTextField source="descriptionHike" label="Description (Hike)" />
      </MultiLangBlock>
      <ReferenceManyToManyField
        linkResName="routeToRegionLink"
        fromResIdName="regionId"
        toResIdName="routeId"
        toResName="route"
        label="Routes (Bike)"
        filter={{ route: { activityType: { _eq: 'bike' } } }}
      />
      <ReferenceManyToManyField
        linkResName="routeToRegionLink"
        fromResIdName="regionId"
        toResIdName="routeId"
        toResName="route"
        label="Routes (Hike)"
        filter={{ route: { activityType: { _eq: 'hike' } } }}
      />
      <ReferenceManyToManyField
        linkResName="hotelToRegionLink"
        fromResIdName="regionId"
        toResIdName="hotelId"
        toResName="hotel"
        label="Hotels"
      />
      <ReferenceManyToManyField
        linkResName="bikeShopToRegionLink"
        fromResIdName="regionId"
        toResIdName="bikeShopId"
        toResName="bikeShop"
        label="Bike Shops"
      />
      <ReferenceManyToManyField
        linkResName="poiToRegionLink"
        fromResIdName="regionId"
        toResIdName="pointOfInterestId"
        toResName="pointOfInterest"
        label="Points of interest"
      />
    </SimpleShowLayout>
  </Show>
);

export const RegionEdit = props => (
  <Edit title={<RegionTitle />} {...props}>
    <ManyToManyInputHelper
      configs={[
        {
          source: 'routes',
          linkResName: 'routeToRegionLink',
          fromResIdName: 'regionId',
          toResIdName: 'routeId',
          toResName: 'route',
          label: 'Routes',
          optionText: <RouteOptionRenderer />,
          sortChoices: routes => {
            return routes.sort((a, b) => {
              // First sort by activity type (bike first, then hike)
              if (a.activityType !== b.activityType) {
                return a.activityType === 'bike' ? -1 : 1;
              }
              // Then sort by internal name
              return (a.internalName || '').localeCompare(b.internalName || '');
            });
          },
        },
        {
          source: 'hotels',
          linkResName: 'hotelToRegionLink',
          fromResIdName: 'regionId',
          toResIdName: 'hotelId',
          toResName: 'hotel',
          label: 'Hotels',
        },
        {
          source: 'bikeShops',
          linkResName: 'bikeShopToRegionLink',
          fromResIdName: 'regionId',
          toResIdName: 'bikeShopId',
          toResName: 'bikeShop',
          label: 'Bike Shops',
        },
        {
          source: 'pois',
          linkResName: 'poiToRegionLink',
          fromResIdName: 'regionId',
          toResIdName: 'pointOfInterestId',
          toResName: 'pointOfInterest',
          label: 'Points of interest',
        },
      ]}
    >
      {(
        editProps,
        defaultValue,
        [RoutesInput, HotelsInput, BikeShopsInput, PointsOfInterestInput],
      ) => (
        <SimpleForm defaultValue={defaultValue} {...editProps}>
          <TextInput source="name" validate={[required()]} />
          <LLRUploadInput
            source="image"
            type="image"
            label="Image (Bike) (720x780)"
            validate={[required()]}
          />
          <LLRUploadInput source="imageHike" type="image" label="Image (Hike) (720x780)" />
          <MultiLangBlock>
            <LongTextInput
              source="description"
              label="Description (Bike)"
              validate={[required()]}
            />
          </MultiLangBlock>
          <MultiLangBlock>
            <LongTextInput source="descriptionHike" label="Description (Hike)" />
          </MultiLangBlock>
          <RoutesInput />
          <HotelsInput />
          <BikeShopsInput />
          <PointsOfInterestInput />
        </SimpleForm>
      )}
    </ManyToManyInputHelper>
  </Edit>
);

export const RegionCreate = props => (
  <Create {...props}>
    <SimpleForm>
      <TextInput source="name" validate={[required()]} autoFocus />
      <LLRUploadInput
        source="image"
        type="image"
        label="Image (Bike) (720x780)"
        validate={[required()]}
      />
      <LLRUploadInput source="imageHike" type="image" label="Image (Hike) (720x780)" />
      <MultiLangBlock>
        <LongTextInput source="description" label="Description (Bike)" validate={[required()]} />
      </MultiLangBlock>
      <MultiLangBlock>
        <LongTextInput source="descriptionHike" label="Description (Hike)" />
      </MultiLangBlock>
    </SimpleForm>
  </Create>
);
